import { useEffect } from 'react';
import { usePublicSettings } from './usePublicSettings';

const SITE_NAME = 'YEKÜV — 21. Yüzyıl Eğitim ve Kültür Vakfı';
const DEFAULT_DESC = '1992\'den bu yana eğitimde fırsat eşitliği için çalışan vakıf. Bağış yapın, gönüllü olun, hayata dokunun.';
const DEFAULT_OG = '/logos/yekuv-og.jpg';

// Per-page defaults (kept in sync with AdminSEO PAGES). Used when admin hasn't
// saved a custom value yet, so public users still see context-aware titles.
export const PAGE_SEO_DEFAULTS = {
  home: { title: 'YEKÜV — Bir Çocuk Daha Okusun Diye', description: '1992\'den bu yana 12.500+ bursiyere ulaşan vakıf. Bağışlarınızla yarına dokunun.' },
  about: { title: 'Hakkımızda', description: '21. Yüzyıl Eğitim ve Kültür Vakfı\'nın tarihçesi, vizyonu, misyonu ve yönetim kurulu.' },
  presidents: { title: 'Başkanlarımız', description: 'Vakfımızın kuruluşundan bugüne hizmet eden değerli başkanlarımız.' },
  boards: { title: 'Kurullarımız', description: 'Yönetim, denetim ve danışma kurullarımız.' },
  media: { title: 'Medyada YEKÜV', description: 'Vakfımızın basında, televizyonda ve dijital medyada yer alan haberleri.' },
  units: { title: 'Eğitim Destek Birimlerimiz', description: 'İstanbul\'un farklı ilçelerinde hizmet veren eğitim merkezlerimiz.' },
  sponsors: { title: 'Destekçilerimiz', description: 'YEKÜV\'ü destekleyen kurumlar, vakıflar ve gönüllüler.' },
  school: { title: 'YEKÜV Nilüfer Gökay Ortaokulu', description: 'Vakfımızın eğitim mirasını taşıyan ortaokulumuz.' },
  tax: { title: 'Kamu Yararına Vergi Muafiyeti', description: 'YEKÜV bağışlarınız için vergi avantajı sağlar.' },
  kvkk: { title: 'KVKK Aydınlatma Metni', description: '6698 sayılı kanun kapsamında kişisel verilerin korunması politikamız.' },
  donate: { title: 'Online Bağış', description: 'Güvenli ödeme altyapısıyla anında bağış yapın. Hayata dokunmaya başlayın.' },
  donation_types: { title: 'Bağış Türlerimiz', description: 'Genel bağış, özel gün, proje bağışı, devre bursu ve aylık eğitim bursu.' },
  special_day: { title: 'Özel Gün Bağışı', description: 'Doğum günü, yıl dönümü, anma için sevdiklerinize anlamlı bir hediye.' },
  donation_channels: { title: 'Bağış Kanallarımız', description: 'Banka hesapları, IBAN bilgileri ve diğer bağış yolları.' },
  scholarships: { title: 'Burslarımız', description: 'Üniversite, lise ve ortaokul öğrencileri için karşılıksız eğitim bursu başvurusu.' },
  projects: { title: 'Projeler & Etkinliklerimiz', description: 'Devam eden ve tamamlanan projelerimiz, sosyal & kültürel etkinliklerimiz.' },
  news: { title: 'Duyurular & Haberler', description: 'Vakfımızdan en güncel haberler, etkinlikler ve duyurular.' },
  contact: { title: 'İletişim', description: 'YEKÜV\'e ulaşmanın yolları: telefon, e-posta, adres ve sosyal medya.' },
  volunteer: { title: 'Gönüllü Olun', description: 'Bireysel veya kurumsal olarak vakfımızın gönüllüsü olun, fark yaratın.' },
};

function setMeta(name, content, isProperty = false) {
  if (!content) return;
  const sel = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement('meta');
    if (isProperty) el.setAttribute('property', name);
    else el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * useSEO('home') — sets <title>, description, OG/Twitter tags from
 * settings keys: seo_<page>_title, seo_<page>_description, seo_<page>_og_image, seo_<page>_keywords.
 * Falls back to defaults if admin hasn't set anything.
 */
export function useSEO(pageKey, fallback = {}) {
  const s = usePublicSettings();

  useEffect(() => {
    const def = PAGE_SEO_DEFAULTS[pageKey] || {};
    const title = s[`seo_${pageKey}_title`] || fallback.title || def.title;
    const desc = s[`seo_${pageKey}_description`] || fallback.description || def.description || DEFAULT_DESC;
    const og = s[`seo_${pageKey}_og_image`] || fallback.og_image || DEFAULT_OG;
    const keywords = s[`seo_${pageKey}_keywords`] || fallback.keywords || '';

    const finalTitle = title ? `${title} · YEKÜV` : SITE_NAME;
    document.title = finalTitle;

    setMeta('description', desc);
    if (keywords) setMeta('keywords', keywords);

    // Open Graph
    setMeta('og:title', finalTitle, true);
    setMeta('og:description', desc, true);
    setMeta('og:type', 'website', true);
    setMeta('og:site_name', 'YEKÜV', true);
    setMeta('og:url', window.location.href, true);
    if (og) {
      const absOg = og.startsWith('http') ? og : window.location.origin + og;
      setMeta('og:image', absOg, true);
    }

    // Twitter
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', finalTitle);
    setMeta('twitter:description', desc);
    if (og) {
      const absOg = og.startsWith('http') ? og : window.location.origin + og;
      setMeta('twitter:image', absOg);
    }

    // Canonical
    setLink('canonical', window.location.href.split('?')[0].split('#')[0]);
  }, [s, pageKey, fallback.title, fallback.description, fallback.og_image, fallback.keywords]);
}

export default useSEO;
